import { effect, signal } from "@preact/signals";

import * as genericModals from "genericModals";
import LatestRequest from "LatestRequest";
import readlangClient from "readlangClient";
import BookStore, {
  BookData,
  isUserTheBookOwner,
  readingProgress,
} from "stores/BookStore";
import SelectionStore from "stores/SelectionStore";

export interface BookFetchQuery {
  [key: string]: any;
}

export interface BookFetchOptions {
  append?: boolean;
  totalCount?: number;
}

export interface BookRestoredNotice {
  bookID: string;
  sequence: number;
}

export interface DeleteAllNotice {
  tag: string;
  sequence: number;
}

type BookSelection = SelectionStore;

export default class BookCollectionStore {
  readonly books = signal<BookStore[]>([]);
  readonly totalCountSignal = signal(0);
  readonly restoredBook = signal<BookRestoredNotice | null>(null);
  readonly deletedAll = signal<DeleteAllNotice | null>(null);
  readonly selectionStore: SelectionStore;

  private noticeSequence = 0;
  /** Fetches replacing the list (not adding a page to it) */
  private readonly listFetch = new LatestRequest();
  private visibilityFilter: ((book: BookStore) => boolean) | null = null;

  constructor(books: BookData[] = []) {
    this.selectionStore = new SelectionStore(this);
    this.setBooks(books);
  }

  /** Shows reading progress as it changes (in the reader): returns the means to stop */
  followReadingProgress() {
    return effect(() => {
      const progress = readingProgress.value;
      if (progress) {
        // Once the change has been notified, not while it is
        void Promise.resolve().then(() =>
          this.books
            .peek()
            .find((book) => book.id === progress.bookID)
            ?.showUserBook(progress.userBook)
        );
      }
    });
  }

  get length() {
    return this.books.value.length;
  }

  get totalCount() {
    return this.totalCountSignal.value;
  }

  setVisibilityFilter(filter: ((book: BookStore) => boolean) | null) {
    this.visibilityFilter = filter;
    const visibleIDs = new Set(this.getWordIDs());
    const itemIDs = this.selectionStore.itemIDs.value;
    const hidden = itemIDs.filter((id) => !visibleIDs.has(id));
    if (hidden.length > 0) {
      this.selectionStore.removeItems(hidden);
    }
    const remaining = this.selectionStore.itemIDs.value;
    if (
      visibleIDs.size > 0 &&
      remaining.length === visibleIDs.size &&
      remaining.every((id) => visibleIDs.has(id))
    ) {
      this.selectionStore.wholePage.value = true;
    }
  }

  visibleBooks(): BookStore[] {
    const filter = this.visibilityFilter;
    if (!filter) return this.books.value;
    return this.books.value.filter(filter);
  }

  getWordIDs(): string[] {
    return this.visibleBooks().map((book) => book.id);
  }

  reset() {
    this.books.value = [];
    this.totalCountSignal.value = 0;
    this.selectionStore.clearSelection();
  }

  setBooks(books: BookData[]) {
    const bookStores = books.map((book) => new BookStore(book));
    this.books.value = bookStores;
    this.totalCountSignal.value = bookStores.length;
    this.selectionStore.clearSelection();
  }

  async fetch(query: BookFetchQuery = {}, options: BookFetchOptions = {}) {
    const request = readlangClient.request<BookData[]>(
      "GET",
      "/api/books",
      query
    );
    const response = await (options.append
      ? request
      : this.listFetch.send(request));
    if (!response) {
      return [];
    }
    const fetchedBooks = response.data.map((book) => new BookStore(book));

    if (options.append) {
      this.books.value = [...this.books.value, ...fetchedBooks];
    } else {
      this.books.value = fetchedBooks;
      this.selectionStore.clearSelection();
    }

    this.totalCountSignal.value = options.totalCount ?? this.books.value.length;
    return fetchedBooks;
  }

  get(bookID: string) {
    return this.books.value.find((book) => book.id === bookID);
  }

  pluck(attr: string): any[] {
    if (attr === "_id") {
      return this.books.value.map((book) => book.id);
    }
    return this.books.value.map((book) => book.book.value[attr]);
  }

  removeBook(bookID: string) {
    this.books.value = this.books.value.filter((book) => book.id !== bookID);
    this.totalCountSignal.value = this.books.value.length;
    this.selectionStore.removeItem(bookID);
  }

  performAction(actionID: string, selection: BookSelection) {
    const actions: Record<string, (targetSelection: BookSelection) => void> = {
      archiveTexts: this.archiveTexts.bind(this),
      restoreToArchive: this.restoreToArchive.bind(this),
      deleteTexts: this.deleteTexts.bind(this),
      addToReadingQueue: this.addToReadingQueue.bind(this),
      restoreToReadingQueue: this.restoreToReadingQueue.bind(this),
      markAsCompleted: this.markAsCompleted.bind(this),
      restoreToCompleted: this.restoreToCompleted.bind(this),
      permanentlyDeleteTexts: this.permanentlyDeleteTexts.bind(this),
      removeFromShelf: this.removeFromShelf.bind(this),
      share: this.share.bind(this),
      shareUnlisted: this.shareUnlisted.bind(this),
      unShare: this.unShare.bind(this),
    };

    const action = actions[actionID];
    if (action) {
      action(selection);
    } else {
      console.error("action not available: ", actionID);
    }
  }

  async deleteAll(options: { tag: "abandoned"; language: string }) {
    console.assert(options.tag === "abandoned");

    this.reset();
    this.deletedAll.value = {
      tag: options.tag,
      sequence: ++this.noticeSequence,
    };

    await readlangClient.request("DELETE", "/api/books", {
      tag: options.tag,
      language: options.language,
    });
  }

  async addTagToSelection(
    tagName: string,
    selection: BookSelection = this.selectionStore,
    options: { hide?: boolean } = {}
  ) {
    await Promise.all(
      this.selectedBooks(selection).map(async (book) => {
        if ((book.book.value.userBook?.tags ?? []).length === 0) {
          this.restoredBook.value = {
            bookID: book.id,
            sequence: ++this.noticeSequence,
          };
        }
        if (!book.hasTag(tagName)) {
          await book.addTag(tagName, options);
        }
      })
    );
  }

  async removeTagFromSelection(
    tagName: string,
    selection: BookSelection = this.selectionStore
  ) {
    await Promise.all(
      this.selectedBooks(selection).map(async (book) => {
        await book.removeTag(tagName);
      })
    );
  }

  archiveTexts(selection: BookSelection = this.selectionStore) {
    void this.addTagToSelection("abandoned", selection);
  }

  restoreToArchive(selection: BookSelection = this.selectionStore) {
    void this.addTagToSelection("abandoned", selection);
  }

  async deleteTexts(selection: BookSelection = this.selectionStore) {
    await Promise.all(
      this.selectedBooks(selection).map(async (book) => {
        const tags = [...(book.book.value.userBook?.tags ?? [])];
        for (const tag of tags) {
          await book.removeTag(tag);
        }
      })
    );
  }

  addToReadingQueue(selection: BookSelection = this.selectionStore) {
    void this.addTagToSelection("queue", selection);
  }

  restoreToReadingQueue(selection: BookSelection = this.selectionStore) {
    void this.addTagToSelection("queue", selection);
  }

  markAsCompleted(selection: BookSelection = this.selectionStore) {
    void this.addTagToSelection("completed", selection);
  }

  restoreToCompleted(selection: BookSelection = this.selectionStore) {
    void this.addTagToSelection("completed", selection);
  }

  permanentlyDeleteTexts(selection: BookSelection = this.selectionStore) {
    const selectedBooks = this.selectedBooks(selection);
    const publicBooks = selectedBooks.filter((book) => {
      const userBook = book.book.value.userBook;
      return userBook && !isUserTheBookOwner(book.book.value);
    });

    let extraMessage = "";
    if (publicBooks.length === 1) {
      extraMessage =
        "\n\nNote: this book will remain in the **public library**:\n- " +
        publicBooks.map((book) => book.book.value.title).join("\n-");
    } else if (publicBooks.length > 1) {
      extraMessage =
        "\n\nNote: these books will remain in the **public library**:\n- " +
        publicBooks.map((book) => book.book.value.title).join("\n-");
    }

    genericModals.confirm({
      title: "Permanently Delete Texts",
      warning: true,
      message:
        "Are you sure you want to **permanently delete** these texts from My Texts?" +
        extraMessage,
      callback: (response: string) => {
        if (response === "ok") {
          void this.destroyBooks(selectedBooks);
        }
      },
    });
  }

  deleteAudioFromSelection(selection: BookSelection = this.selectionStore) {
    const booksWithAudio = this.selectedBooks(selection).filter((book) =>
      book.hasAudio()
    );
    if (booksWithAudio.length === 0) return;

    const single = booksWithAudio.length === 1;
    genericModals.confirm({
      title: "Delete Audio",
      warning: true,
      message:
        "Are you sure you want to **permanently delete** the audio from " +
        (single ? "this text" : "these " + booksWithAudio.length + " texts") +
        "? This can't be undone.",
      callback: (response: string) => {
        if (response === "ok") {
          void Promise.all(booksWithAudio.map((book) => book.deleteAudio()));
        }
      },
    });
  }

  removeFromShelf() {
    console.log("TODO: implement");
  }

  share(selection: BookSelection = this.selectionStore) {
    void this.addTagToSelection("shared", selection);
  }

  shareUnlisted(selection: BookSelection = this.selectionStore) {
    void this.addTagToSelection("shared", selection, { hide: true });
  }

  unShare(selection: BookSelection = this.selectionStore) {
    genericModals.confirm({
      title: "Un-share text" + (selection.count.value === 1 ? "" : "s"),
      message:
        "Are you sure you want to un-share " +
        (selection.count.value === 1 ? "this" : "these") +
        "?\n\nIt may inconvenience other users who will no longer be able to read it.",
      callback: (response: string) => {
        if (response === "ok") {
          void this.removeTagFromSelection("shared", selection);
        }
      },
    });
  }

  private selectedBooks(selection: BookSelection) {
    return selection
      .getSelectedItems()
      .filter((book): book is BookStore => !!book);
  }

  private async destroyBooks(books: BookStore[]) {
    for (const book of books) {
      await book.destroyBook();
      this.removeBook(book.id);
    }
  }
}
